'use client';

import { useAppRouter as useRouter } from '@/lib/navigation';
import { ArrowRight } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';
import type { ProcessHistoryTaskItem } from './process-history-types';

interface ProcessHistoryTaskListProps {
  processInstanceId: string;
  tasks: ProcessHistoryTaskItem[];
}

export function ProcessHistoryTaskList({
  processInstanceId,
  tasks,
}: ProcessHistoryTaskListProps) {
  const router = useRouter();

  const navigateToDetail = (taskId: string) => {
    router.push(`/task-list/participated/${processInstanceId}/tasks/${taskId}`);
  };

  if (tasks.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        No hay formularios registrados en este caso.
      </p>
    );
  }

  return (
    <div className="overflow-hidden rounded-lg border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Tarea</TableHead>
            <TableHead>Estado</TableHead>
            <TableHead>Asignado</TableHead>
            <TableHead>Inicio</TableHead>
            <TableHead>Fin</TableHead>
            <TableHead className="w-[140px]">Acciones</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {tasks.map((item) => {
            const canViewDetail = item.canViewDetail;

            return (
              <TableRow
                key={item.task.id}
                role={canViewDetail ? 'button' : undefined}
                tabIndex={canViewDetail ? 0 : undefined}
                className={cn(
                  canViewDetail &&
                  'cursor-pointer hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  !canViewDetail && 'opacity-60',
                )}
                onClick={
                  canViewDetail
                    ? () => navigateToDetail(item.task.id)
                    : undefined
                }
                onKeyDown={
                  canViewDetail
                    ? (event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        navigateToDetail(item.task.id);
                      }
                    }
                    : undefined
                }
              >
                <TableCell className="font-medium">{item.displayName}</TableCell>
                <TableCell>
                  <Badge
                    variant="outline"
                    className={cn('text-[11px]', item.stateClassName)}
                  >
                    {item.stateLabel}
                  </Badge>
                </TableCell>
                <TableCell>{item.assigneeName}</TableCell>
                <TableCell>{new Date(item.task.startTime).toLocaleString()}</TableCell>
                <TableCell>
                  {item.task.endTime ? new Date(item.task.endTime).toLocaleString() : '-'}
                </TableCell>
                <TableCell>
                  {item.workableTaskId ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(event) => {
                        event.stopPropagation();
                        router.push(`/task-list/tasks/${item.workableTaskId}`);
                      }}
                    >
                      Ir a trabajar
                      <ArrowRight className="ml-1 h-4 w-4" />
                    </Button>
                  ) : !canViewDetail ? (
                    <span className="text-xs text-muted-foreground">Sin acceso al detalle</span>
                  ) : null}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
